import React, { useState } from 'react';
import { useGetOrdersQuery, useUpdateOrderStatusMutation, useDeleteOrderMutation, useExportOrdersCSVMutation } from '../../services/api';
import { formatCurrency, formatDateTime, getStatusColor, getStatusText } from '../../utils/helpers';
import ProtectedRoute from '../../components/ProtectedRoute/ProtectedRoute';
import Loading from '../../components/Loading/Loading';
import Error from '../../components/Error/Error';
import './Orders.css';

const Orders = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    status: ''
  });

  const { data, isLoading, error, refetch } = useGetOrdersQuery(filters);
  const [updateStatus] = useUpdateOrderStatusMutation();
  const [deleteOrder] = useDeleteOrderMutation();
  const [exportCSV] = useExportOrdersCSVMutation();

  const orders = data?.data?.orders || [];
  const pagination = data?.data?.pagination;

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateStatus({ orderId, status: newStatus }).unwrap();
    } catch (error) {
      console.error('Failed to update order status:', error);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await deleteOrder(orderId).unwrap();
      } catch (error) {
        console.error('Failed to delete order:', error);
      }
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await exportCSV().unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'orders.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  const handlePrintReceipt = (orderId) => {
    const receiptUrl = `${process.env.REACT_APP_API_BASE}/owner/orders/${orderId}/receipt`;
    window.open(receiptUrl, '_blank');
  };

  if (isLoading) {
    return <Loading text="Loading orders..." />;
  }

  if (error) {
    return <Error message="Failed to load orders" onRetry={refetch} />;
  }

  return (
    <ProtectedRoute roles={['OWNER']}>
      <div className="container">
        <div className="page-header">
          <h1>Orders Management</h1>
          <div className="header-actions">
            <button onClick={handleExportCSV} className="btn btn-secondary">
              Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSearch} className="filters-form">
              <div className="filters-grid">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Search by Order ID or Customer..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="form-control"
                  >
                    <option value="">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="PREPARING">Preparing</option>
                    <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>

                <button type="submit" className="btn btn-primary">
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Orders Table */}
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order._id}>
                    <td>
                      <strong>{order.orderNumber}</strong>
                    </td>
                    <td>
                      <div>
                        <div>{order.customerId?.name}</div>
                        <div className="text-sm text-muted">{order.customerId?.email}</div>
                      </div>
                    </td>
                    <td>
                      <div className="order-items">
                        {order.items.slice(0, 2).map((item, index) => (
                          <div key={index} className="order-item">
                            {item.nameSnapshot} × {item.qty}
                          </div>
                        ))}
                        {order.items.length > 2 && (
                          <div className="text-muted">
                            +{order.items.length - 2} more items
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{formatCurrency(order.amount)}</td>
                    <td>
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusUpdate(order._id, e.target.value)}
                        className={`status-select status-${getStatusColor(order.status)}`}
                      >
                        <option value="PENDING">Pending</option>
                        <option value="ACCEPTED">Accepted</option>
                        <option value="PREPARING">Preparing</option>
                        <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="CANCELLED">Cancelled</option>
                      </select>
                    </td>
                    <td>{formatDateTime(order.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handlePrintReceipt(order._id)}
                          className="btn btn-sm btn-secondary"
                          title="Print Receipt"
                        >
                          🖨️
                        </button>
                        <button
                          onClick={() => handleDeleteOrder(order._id)}
                          className="btn btn-sm btn-danger"
                          title="Delete Order"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={filters.page === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            
            <span className="pagination-info">
              Page {pagination.page} of {pagination.pages}
            </span>
            
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={filters.page === pagination.pages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default Orders;